import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DEPARTMENT_DEFS, MAX_DUTIES } from '@goyone/shared';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  Notice,
  PageHead,
  SearchBox,
  Stat,
} from '../../components/ui';
import { Icon } from '../../components/Icon';
import { useToast } from '../../components/Toast';
import { api, qs } from '../../lib/api';
import { useDebounced } from '../../lib/hooks';
import { money, num } from '../../lib/format';
import { useAuth } from '../../auth/AuthProvider';

interface GradeRow {
  id: string;
  name: string;
  level: number;
}

interface PositionRow {
  id: string;
  code: string | null;
  title: string;
  deptKey: string | null;
  department: string | null;
  /** موضعُها في الهيكل — يُرسم في «الهيكل التنظيمي» ويُقرأ هنا. */
  orgUnit: { id: string; name: string } | null;
  grade: { id: string; name: string; level: number } | null;
  purpose: string | null;
  duties: string[];
  requirements: string[];
  reportsToId: string | null;
  reportsTo: { id: string; title: string } | null;
  nextPositionId: string | null;
  nextPosition: { id: string; title: string } | null;
  headcount: number;
  filled: number;
  gap: number;
  successorCount: number;
  isActive: boolean;
}

interface Holder {
  id: string;
  name: string;
  code: string;
  salary: number;
  compaRatio: number | null;
  compaLabel: string | null;
  compaKind: string | null;
}

interface Successor {
  id: string;
  employeeId: string;
  name: string;
  code: string;
  readiness: string;
  readinessLabel: string;
  readinessKind: string;
  notes: string | null;
  gaps: string[];
}

interface PositionFull extends PositionRow {
  holders: Holder[];
  successors: Successor[];
}

/* ==========================================================================
   قائمة الأوصاف الوظيفية
   ========================================================================== */

/**
 * الأوصافُ الوظيفية — شكلُ الشركة لا أشخاصُها.
 *
 * «مصمّمٌ أوّل» وظيفةٌ لها وصفٌ ودرجةٌ وعددٌ معتمَد، و«أحمد» موظّفٌ يشغلها.
 * وفصلُهما هو ما يجعل تخطيطَ القوى العاملة ممكنًا أصلًا: بلا عددٍ معتمَدٍ
 * مكتوبٍ في مكانٍ ما لا يوجد ما يُقارَن به عددُ الموجودين.
 *
 * والموقوفةُ تُخفى افتراضًا: الوصفُ الملغى يبقى لأنّ صفوفَ الموظّفين
 * والإحلال تشير إليه، ولكنّه لا يزاحم العاملَ في القائمة.
 */
export function Positions() {
  const { atLeast } = useAuth();
  const canEdit = atLeast('MANAGER');

  const [search, setSearch] = useState('');
  const [deptKey, setDeptKey] = useState('');
  const [gradeId, setGradeId] = useState('');
  const [all, setAll] = useState(false);
  const [editing, setEditing] = useState<PositionRow | 'new' | null>(null);

  /* البحثُ يُؤخَّر: كلُّ حرفٍ طلبٌ، وقد تصل استجابةٌ قديمةٌ بعد أحدثَ منها. */
  const q = useDebounced(search);

  const query = {
    deptKey: deptKey || undefined,
    gradeId: gradeId || undefined,
    q: q.trim() || undefined,
    all: all ? '1' : undefined,
  };

  const list = useQuery({
    queryKey: ['hr', 'org', 'positions', query],
    queryFn: () => api<PositionRow[]>(`/hr/org/positions${qs(query)}`),
  });

  const grades = useQuery({
    queryKey: ['hr', 'org', 'grades'],
    queryFn: () => api<GradeRow[]>('/settings/grades'),
  });

  const rows = list.data ?? [];
  const narrowed = !!(deptKey || gradeId || q.trim());

  return (
    <div className="page">
      <PageHead
        title="الأوصاف الوظيفية"
        subtitle="أيّ وظائفَ عندنا، وما وصفُ كلٍّ منها، ومن يتبع من"
        actions={
          canEdit && (
            <button
              className="btn btn-primary"
              onClick={() => setEditing('new')}
            >
              <Icon name="plus" size={15} />
              وصف وظيفي جديد
            </button>
          )
        }
      />

      {list.isError && (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      )}

      {/*
        الحدُّ يُكتب لا يُكتشف: «المشغول» عددُ من يشير ملفُّه إلى هذا الوصف،
        فمن يؤدّي العملَ ولم يُربط به لا يُحسب — ويظهر الوصفُ ناقصًا وهو
        مشغول. والعلاجُ ربطُ الموظّف بوصفه في ملفّه لا هنا.
      */}
      <Notice title="«المشغول» من رُبط بالوصف في ملفّه" icon="info">
        العددُ يُحسب من ملفّات الموظّفين: من يشير ملفُّه إلى هذا الوصف يُعدّ
        شاغلًا له. ومن يؤدّي العملَ ولم يُربط به لن يُحسب، فيبدو الوصفُ
        ناقصًا وهو مشغول — يُضبط ذلك من ملفّ الموظّف.
      </Notice>

      <div className="toolbar">
        <SearchBox
          value={search}
          onChange={setSearch}
          placeholder="ابحث بمسمّى الوظيفة"
        />

        <select
          className="select"
          value={deptKey}
          onChange={(e) => setDeptKey(e.target.value)}
          aria-label="القسم"
        >
          <option value="">كل الأقسام</option>
          {DEPARTMENT_DEFS.map((d) => (
            <option key={d.key} value={d.key}>
              {d.label}
            </option>
          ))}
        </select>

        <select
          className="select"
          value={gradeId}
          onChange={(e) => setGradeId(e.target.value)}
          aria-label="الدرجة"
        >
          <option value="">كل الدرجات</option>
          {(grades.data ?? []).map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </select>

        <label className="row tiny" style={{ gap: 6 }}>
          <input
            type="checkbox"
            checked={all}
            onChange={(e) => setAll(e.target.checked)}
          />
          أظهر الموقوفة
        </label>
      </div>

      {list.isPending && <div className="skeleton" style={{ height: 200 }} />}

      {list.data && rows.length === 0 && (
        <EmptyState
          title={narrowed ? 'لا وصف يطابق البحث' : 'لا أوصاف وظيفية بعد'}
          text={
            narrowed
              ? 'جرّب مسمًّى آخر، أو أزل التصفية — والموقوفةُ مخفيّةٌ ما لم تُطلب.'
              : 'الوصفُ الوظيفيُّ هو ما يُقارَن به عددُ الموجودين. وبلا وصفٍ له عددٌ معتمَدٌ يصير «هل ينقصنا أحد؟» سؤالًا يُجاب بالانطباع.'
          }
          action={
            canEdit && !narrowed ? (
              <button
                className="btn btn-primary"
                onClick={() => setEditing('new')}
              >
                اكتب أوّل وصف
              </button>
            ) : undefined
          }
        />
      )}

      {rows.length > 0 && (
        <div className="card">
          <div className="table-wrap sticky-head">
            <table className="table">
              <thead>
                <tr>
                  <th>الوظيفة</th>
                  <th>القسم</th>
                  <th>الدرجة</th>
                  <th>المعتمَد / المشغول</th>
                  <th>تتبع</th>
                  <th>الحالة</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <Link
                        to={`/hr/org/positions/${p.id}`}
                        className="cell-strong link"
                      >
                        {p.title}
                      </Link>
                      {p.code && <div className="tiny mute num">{p.code}</div>}
                    </td>
                    <td className="cell-mute">{p.department ?? '—'}</td>
                    <td className="cell-mute">{p.grade?.name ?? '—'}</td>
                    <td>
                      <span className="num">
                        {num(p.filled)} / {num(p.headcount)}
                      </span>
                      {p.gap > 0 && (
                        <div className="tiny" style={{ color: 'var(--danger)' }}>
                          ناقص {num(p.gap)}
                        </div>
                      )}
                    </td>
                    <td className="cell-mute">{p.reportsTo?.title ?? '—'}</td>
                    <td>
                      <Badge kind={p.isActive ? 'success' : 'neutral'}>
                        {p.isActive ? 'معتمَد' : 'موقوف'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing && (
        <PositionDialog
          position={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

/* ==========================================================================
   الوصف الوظيفي مفتوحًا
   ========================================================================== */

/**
 * وصفٌ وظيفيٌّ واحدًا: غرضُه ومهامُّه ومؤهّلاتُه، ومن يشغله، ومن يخلفه.
 *
 * وموضعُ الراتب من الدرجة يُعرض هنا لا في شاشة الرواتب: من يفتح الوصف
 * يسأل «هل من يشغلها في نطاقها؟» — والجوابُ صفٌّ واحدٌ بجانب كلّ اسم.
 */
export function PositionDetail() {
  const { id = '' } = useParams();
  const { atLeast } = useAuth();
  const canEdit = atLeast('MANAGER');
  const [editing, setEditing] = useState(false);

  const detail = useQuery({
    queryKey: ['hr', 'org', 'position', id],
    queryFn: () => api<PositionFull>(`/hr/org/positions/${id}`),
  });

  const p = detail.data;
  const subtitle = p
    ? [p.code, p.department].filter(Boolean).join(' — ') || undefined
    : undefined;

  /* النسبةُ تحتاج وسطًا مضبوطًا للدرجة — وبلا وسطٍ لا تُخترع. */
  const noBand = !!p && p.holders.some((h) => h.compaRatio === null);
  const readyNow = p
    ? p.successors.filter((s) => s.readiness === 'NOW').length
    : 0;

  return (
    <div className="page">
      <PageHead
        title={p?.title ?? 'وصف وظيفي'}
        subtitle={subtitle}
        actions={
          <>
            <Link to="/hr/org/positions" className="btn">
              رجوع
            </Link>
            {canEdit && p && (
              <button className="btn btn-primary" onClick={() => setEditing(true)}>
                <Icon name="edit" size={15} />
                تعديل الوصف
              </button>
            )}
          </>
        }
      />

      {detail.isError && (
        <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />
      )}

      {detail.isPending && <div className="skeleton" style={{ height: 200 }} />}

      {p && (
        <>
          {!p.isActive && (
            <Notice title="هذا الوصف موقوف" icon="alert" kind="warning">
              الموقوفُ لا يُحذف: صفوفُ الموظّفين وخططُ الإحلال تشير إليه. وهو
              لا يظهر في القائمة ولا يُحسب في تخطيط القوى العاملة.
            </Notice>
          )}

          <div className="stats">
            <Stat label="العدد المعتمَد" value={num(p.headcount)} />
            <Stat label="من يشغلها" value={num(p.filled)} />
            <Stat
              label={p.gap < 0 ? 'زائد عن المعتمَد' : 'الناقص'}
              value={p.gap === 0 ? '—' : num(Math.abs(p.gap))}
              note={
                p.gap > 0
                  ? 'شاغرٌ معتمَدٌ لا أحدَ فيه'
                  : p.gap < 0
                    ? 'يُراجَع الاعتمادُ أو التوزيع'
                    : 'المعتمَد مشغول'
              }
            />
            <Stat
              label="المرشّحون للخلافة"
              value={num(p.successors.length)}
              note={readyNow > 0 ? `${num(readyNow)} جاهزٌ الآن` : undefined}
            />
          </div>

          <div className="two-col">
            <div className="col" style={{ gap: 'var(--sp-4)' }}>
              <div className="card">
                <div className="card-head">
                  <h2 className="card-title">غرض الوظيفة</h2>
                </div>
                <div className="card-body">
                  {p.purpose ? (
                    <p>{p.purpose}</p>
                  ) : (
                    <p className="tiny mute">
                      لم يُكتب غرضٌ بعد. وسطرٌ واحدٌ يقول «لماذا توجد هذه
                      الوظيفة» هو ما يُميّزها عمّا يجاورها عند المراجعة.
                    </p>
                  )}
                </div>
              </div>

              <div className="card">
                <div className="card-head">
                  <h2 className="card-title">المهامّ والمسؤوليّات</h2>
                </div>
                <div className="card-body">
                  {p.duties.length > 0 ? (
                    <ul className="od-bullets">
                      {p.duties.map((d, i) => (
                        <li key={i}>{d}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="tiny mute">
                      لا مهامَّ مكتوبة. والوصفُ بلا مهامٍّ لا يصلح تقييمًا ولا
                      إعلانَ توظيف.
                    </p>
                  )}
                </div>
              </div>

              <div className="card">
                <div className="card-head">
                  <h2 className="card-title">المؤهّلات المطلوبة</h2>
                </div>
                <div className="card-body">
                  {p.requirements.length > 0 ? (
                    <ul className="od-bullets">
                      {p.requirements.map((r, i) => (
                        <li key={i}>{r}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="tiny mute">
                      لا مؤهّلاتٍ مكتوبة — وهي ما يُفرز به المرشّحون، وما
                      تُبنى عليه فجواتُ من يُرشَّح للخلافة.
                    </p>
                  )}
                </div>
              </div>

              <div className="card">
                <div className="card-head">
                  <h2 className="card-title">من يشغلها الآن</h2>
                </div>

                {p.holders.length === 0 ? (
                  <div className="card-body">
                    <p className="tiny mute">
                      لا أحدَ مربوطٌ بهذا الوصف. والرابطُ يُضبط من ملفّ
                      الموظّف — لا من هنا.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="table-wrap">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>الموظّف</th>
                            <th>الراتب الأساسي</th>
                            <th>موضعه من الدرجة</th>
                          </tr>
                        </thead>
                        <tbody>
                          {p.holders.map((h) => (
                            <tr key={h.id}>
                              <td>
                                <Link
                                  to={`/hr/employees/${h.id}`}
                                  className="cell-strong link"
                                >
                                  {h.name}
                                </Link>
                                <div className="tiny mute num">{h.code}</div>
                              </td>
                              <td className="num">{money(h.salary)}</td>
                              <td>
                                {h.compaRatio === null ? (
                                  <span className="tiny mute">—</span>
                                ) : (
                                  <span className="row" style={{ gap: 6 }}>
                                    <span className="num">{h.compaRatio}٪</span>
                                    {h.compaLabel && (
                                      <Badge kind={h.compaKind ?? 'neutral'}>
                                        {h.compaLabel}
                                      </Badge>
                                    )}
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/*
                      الفراغُ يُفسَّر: «—» بلا سببٍ يُقرأ عطبًا، وسببُه هنا
                      أنّ الدرجةَ بلا وسطٍ مضبوط، ونسبةٌ إلى صفرٍ لا معنى لها.
                    */}
                    {noBand && (
                      <div className="card-body">
                        <p className="tiny mute">
                          موضعُ الراتب يُقاس إلى وسط نطاق الدرجة. وبلا وسطٍ
                          مضبوطٍ لا نسبةَ تُحسب — يُضبط من{' '}
                          <Link className="link" to="/settings/grades">
                            هيكل الدرجات
                          </Link>
                          .
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="col" style={{ gap: 'var(--sp-4)' }}>
              <div className="card">
                <div className="card-head">
                  <h2 className="card-title">الموضع في الهيكل</h2>
                </div>
                <div className="card-body">
                  <dl className="detail-list">
                    <dt>الكود</dt>
                    <dd className="num">{p.code ?? '—'}</dd>

                    <dt>القسم</dt>
                    <dd>{p.department ?? '—'}</dd>

                    {/*
                      الوحدةُ تُقرأ هنا ولا تُحرَّر: موضعُها يُرسم بالسحب في
                      «الهيكل التنظيمي»، وقائمةٌ منسدلةٌ هنا كانت ستجعل
                      للموضع مصدرين يفترقان.
                    */}
                    <dt>الوحدة</dt>
                    <dd>
                      {p.orgUnit ? (
                        p.orgUnit.name
                      ) : (
                        <span className="mute">
                          خارج الهيكل —{' '}
                          <Link className="link" to="/settings/org-structure">
                            ضعها بالسحب
                          </Link>
                        </span>
                      )}
                    </dd>

                    <dt>الدرجة</dt>
                    <dd>
                      {p.grade ? (
                        <>
                          {p.grade.name}{' '}
                          <span className="tiny mute">
                            المستوى <span className="num">{p.grade.level}</span>
                          </span>
                        </>
                      ) : (
                        <span className="mute">بلا درجة</span>
                      )}
                    </dd>

                    <dt>تتبع</dt>
                    <dd>
                      {p.reportsTo ? (
                        <Link
                          className="link"
                          to={`/hr/org/positions/${p.reportsTo.id}`}
                        >
                          {p.reportsTo.title}
                        </Link>
                      ) : (
                        <span className="mute">لا تتبع أحدًا</span>
                      )}
                    </dd>

                    <dt>الخطوة التالية</dt>
                    <dd>
                      {p.nextPosition ? (
                        <Link
                          className="link"
                          to={`/hr/org/positions/${p.nextPosition.id}`}
                        >
                          {p.nextPosition.title}
                        </Link>
                      ) : (
                        <span className="mute">لم تُحدَّد</span>
                      )}
                    </dd>

                    <dt>الحالة</dt>
                    <dd>
                      <Badge kind={p.isActive ? 'success' : 'neutral'}>
                        {p.isActive ? 'معتمَد' : 'موقوف'}
                      </Badge>
                    </dd>
                  </dl>

                  {!p.nextPosition && (
                    <p className="tiny mute mt-4">
                      «الخطوة التالية» هي ما يُقال للموظّف حين يسأل «وبعد؟».
                      وبلا تحديدها يصير المسارُ الوظيفيُّ وعدًا شفويًّا.
                    </p>
                  )}
                </div>
              </div>

              <div className="card">
                <div className="card-head">
                  <h2 className="card-title">من يخلف من يشغلها</h2>
                </div>

                {p.successors.length === 0 ? (
                  <div className="card-body">
                    <p className="tiny mute">
                      لا مرشّحين. ومن لا خليفةَ له اليومَ يُبحث عن بديله في
                      يوم رحيله — يُضاف المرشّحون من{' '}
                      <Link className="link" to="/hr/org/succession">
                        خطط الإحلال
                      </Link>
                      .
                    </p>
                  </div>
                ) : (
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>المرشّح</th>
                          <th>الجاهزية</th>
                        </tr>
                      </thead>
                      <tbody>
                        {p.successors.map((s) => (
                          <tr key={s.id}>
                            <td>
                              <Link
                                to={`/hr/employees/${s.employeeId}`}
                                className="cell-strong link"
                              >
                                {s.name}
                              </Link>
                              {s.gaps.length > 0 && (
                                <div className="tiny mute">
                                  ينقصه: {s.gaps.join('، ')}
                                </div>
                              )}
                              {s.notes && (
                                <div className="tiny mute">{s.notes}</div>
                              )}
                            </td>
                            <td>
                              <Badge kind={s.readinessKind}>
                                {s.readinessLabel}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {editing && p && (
        <PositionDialog position={p} onClose={() => setEditing(false)} />
      )}
    </div>
  );
}

/* ==========================================================================
   محرّر الوصف الوظيفي
   ========================================================================== */

/**
 * قائمةُ بنودٍ تُحرَّر: تُزاد وتُحذف ويُغيَّر ترتيبُها.
 *
 * والترتيبُ مقصود: المهمّةُ الأولى في القائمة هي ما يُفهم أنّه جوهرُ
 * الوظيفة، فمن يكتب الوصفَ يحتاج أن يرفعها لا أن يعيد كتابةَ السطور.
 */
function Bullets({
  title,
  hint,
  items,
  onChange,
  placeholder,
  addLabel,
}: {
  title: string;
  hint: string;
  items: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
  addLabel: string;
}) {
  function move(i: number, delta: number) {
    const j = i + delta;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    const [row] = next.splice(i, 1);
    next.splice(j, 0, row!);
    onChange(next);
  }

  return (
    <div className="field span-2">
      <h3 className="card-title">
        {title}{' '}
        <span className="tiny mute">
          <span className="num">{items.length}</span> من{' '}
          <span className="num">{MAX_DUTIES}</span>
        </span>
      </h3>

      <p className="tiny mute">{hint}</p>

      {items.length === 0 && (
        <p className="tiny mute mt-2">لا بنود بعد.</p>
      )}

      <div className="col mt-2">
        {items.map((v, i) => (
          <div className="row" key={i}>
            <div className="col" style={{ gap: 2 }}>
              <button
                type="button"
                className="btn btn-icon btn-ghost btn-sm"
                onClick={() => move(i, -1)}
                disabled={i === 0}
                aria-label="أعلى"
              >
                <Icon name="chevronUp" size={14} />
              </button>
              <button
                type="button"
                className="btn btn-icon btn-ghost btn-sm"
                onClick={() => move(i, 1)}
                disabled={i === items.length - 1}
                aria-label="أسفل"
              >
                <Icon name="chevronDown" size={14} />
              </button>
            </div>

            <input
              className="input"
              style={{ flex: '1 1 140px', minWidth: 0 }}
              value={v}
              maxLength={300}
              placeholder={placeholder}
              onChange={(e) =>
                onChange(items.map((x, j) => (i === j ? e.target.value : x)))
              }
            />

            <button
              type="button"
              className="btn btn-icon btn-ghost btn-sm"
              onClick={() => onChange(items.filter((_, j) => j !== i))}
              aria-label="حذف البند"
            >
              <Icon name="trash" size={14} />
            </button>
          </div>
        ))}
      </div>

      <button
        type="button"
        className="btn btn-sm mt-2"
        onClick={() => onChange([...items, ''])}
        disabled={items.length >= MAX_DUTIES}
      >
        <Icon name="plus" size={14} />
        {addLabel}
      </button>
    </div>
  );
}

/**
 * كتابةُ الوصف الوظيفي أو تعديلُه.
 *
 * «تتبع» و«الخطوة التالية» اختيارٌ من الأوصاف الأخرى لا نصٌّ يُكتب: النصُّ
 * يُملأ بمسمًّى مقارب فلا يُبنى منه هيكلٌ ولا مسار. والوصفُ لا يتبع نفسَه،
 * فيُستبعد من قائمته.
 */
function PositionDialog({
  position,
  onClose,
}: {
  position: PositionRow | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [code, setCode] = useState(position?.code ?? '');
  const [title, setTitle] = useState(position?.title ?? '');
  const [deptKey, setDeptKey] = useState(position?.deptKey ?? '');
  const [gradeId, setGradeId] = useState(position?.grade?.id ?? '');
  const [purpose, setPurpose] = useState(position?.purpose ?? '');
  const [duties, setDuties] = useState<string[]>(position?.duties ?? []);
  const [requirements, setRequirements] = useState<string[]>(
    position?.requirements ?? [],
  );
  const [reportsToId, setReportsToId] = useState(position?.reportsToId ?? '');
  const [nextPositionId, setNextPositionId] = useState(
    position?.nextPositionId ?? '',
  );
  const [headcount, setHeadcount] = useState(String(position?.headcount ?? 1));
  const [isActive, setIsActive] = useState(position?.isActive ?? true);

  const grades = useQuery({
    queryKey: ['hr', 'org', 'grades'],
    queryFn: () => api<GradeRow[]>('/settings/grades'),
  });

  /* الموقوفةُ تدخل هنا: وصفٌ قائمٌ قد يتبع وصفًا أُوقف، ولا يُخفى عنه. */
  const all = useQuery({
    queryKey: ['hr', 'org', 'positions', { all: '1' }],
    queryFn: () => api<PositionRow[]>(`/hr/org/positions${qs({ all: '1' })}`),
  });

  const others = (all.data ?? []).filter((p) => p.id !== position?.id);

  /* الفراغُ ليس صفرًا: حقلٌ خالٍ يُحفظ صفرًا يجعل الوظيفةَ غيرَ معتمَدةٍ بلا قصد. */
  const headcountNum = Number(headcount);
  const valid =
    title.trim().length >= 2 &&
    headcount.trim() !== '' &&
    Number.isInteger(headcountNum) &&
    headcountNum >= 0 &&
    headcountNum <= 999;

  const save = useMutation({
    mutationFn: () => {
      const body = {
        code: code.trim() || null,
        title: title.trim(),
        deptKey: deptKey || null,
        gradeId: gradeId || null,
        purpose: purpose.trim() || null,
        /* السطورُ الفارغةُ تُسقط هنا لا تُرسل: بندٌ فارغٌ يردّه الخادم كلَّه. */
        duties: duties.map((d) => d.trim()).filter(Boolean),
        requirements: requirements.map((r) => r.trim()).filter(Boolean),
        reportsToId: reportsToId || null,
        nextPositionId: nextPositionId || null,
        headcount: headcountNum,
        isActive,
      };
      return position
        ? api(`/hr/org/positions/${position.id}`, { method: 'PATCH', body })
        : api('/hr/org/positions', { method: 'POST', body });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['hr'] });
      toast(position ? 'حُفظ الوصف' : 'أُضيف الوصف');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  return (
    <Modal
      title={position ? `تعديل ${position.title}` : 'وصف وظيفي جديد'}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={!valid || save.isPending}
          >
            {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field">
          <label className="label req" htmlFor="op-title">
            المسمّى الوظيفي
          </label>
          <input
            id="op-title"
            className="input"
            value={title}
            maxLength={160}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="مصمّم جرافيك أوّل"
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="op-code">
            الكود
          </label>
          <input
            id="op-code"
            className="input num"
            dir="ltr"
            value={code}
            maxLength={30}
            onChange={(e) => setCode(e.target.value)}
            placeholder="CRE-02"
          />
          <span className="tiny mute">
            اختياري — يُفيد من يربط الأوصافَ بملفّاتٍ خارج النظام.
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="op-dept">
            القسم
          </label>
          <select
            id="op-dept"
            className="select"
            value={deptKey}
            onChange={(e) => setDeptKey(e.target.value)}
          >
            <option value="">بلا قسم</option>
            {DEPARTMENT_DEFS.map((d) => (
              <option key={d.key} value={d.key}>
                {d.label}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="label" htmlFor="op-grade">
            الدرجة
          </label>
          <select
            id="op-grade"
            className="select"
            value={gradeId}
            onChange={(e) => setGradeId(e.target.value)}
          >
            <option value="">بلا درجة</option>
            {(grades.data ?? []).map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
          <span className="tiny mute">
            بلا درجةٍ لا يُقاس موضعُ راتب من يشغلها من نطاقه.
          </span>
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="op-purpose">
            غرض الوظيفة
          </label>
          <textarea
            id="op-purpose"
            className="textarea"
            value={purpose}
            maxLength={2000}
            onChange={(e) => setPurpose(e.target.value)}
            placeholder="لماذا توجد هذه الوظيفة، وما الذي يختلّ لو خلت."
          />
        </div>

        <Bullets
          title="المهامّ والمسؤوليّات"
          hint="ما يُطالَب به شاغلُها فعلًا — وهو ما يُقيَّم عليه لاحقًا."
          items={duties}
          onChange={setDuties}
          placeholder="يراجع تصاميم الفريق قبل تسليمها للعميل"
          addLabel="أضف مهمّة"
        />

        <Bullets
          title="المؤهّلات المطلوبة"
          hint="ما يُفرز به المرشّحون، وما تُبنى عليه فجواتُ من يُرشَّح للخلافة."
          items={requirements}
          onChange={setRequirements}
          placeholder="خبرة ثلاث سنوات في وكالة إعلان"
          addLabel="أضف مؤهّلًا"
        />

        <div className="field">
          <label className="label" htmlFor="op-reports">
            تتبع
          </label>
          <select
            id="op-reports"
            className="select"
            value={reportsToId}
            onChange={(e) => setReportsToId(e.target.value)}
          >
            <option value="">لا تتبع أحدًا</option>
            {others.map((o) => (
              <option key={o.id} value={o.id}>
                {o.title}
              </option>
            ))}
          </select>
          <span className="tiny mute">
            الوظيفةُ تتبع وظيفةً لا شخصًا — فلا يتغيّر الهيكلُ كلّما تغيّر
            شاغلُ المنصب.
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="op-next">
            الخطوة التالية في المسار
          </label>
          <select
            id="op-next"
            className="select"
            value={nextPositionId}
            onChange={(e) => setNextPositionId(e.target.value)}
          >
            <option value="">لم تُحدَّد</option>
            {others.map((o) => (
              <option key={o.id} value={o.id}>
                {o.title}
              </option>
            ))}
          </select>
          <span className="tiny mute">
            إلى أين يترقّى من يشغلها — وهو الجوابُ حين يُسأل «وبعد؟».
          </span>
        </div>

        <div className="field">
          <label className="label req" htmlFor="op-headcount">
            العدد المعتمَد
          </label>
          <input
            id="op-headcount"
            className="input num"
            type="number"
            min={0}
            max={999}
            value={headcount}
            onChange={(e) => setHeadcount(e.target.value)}
          />
          <span className="tiny mute">
            كم شخصًا اعتُمد لهذه الوظيفة — وهو ما يُقارَن به عددُ الموجودين
            في تخطيط القوى العاملة.
          </span>
        </div>

        <div className="field">
          <label className="row tiny" style={{ gap: 6 }}>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            معتمَد
          </label>
          <span className="tiny mute">
            الإيقافُ لا الحذف — ملفّاتُ الموظّفين وخططُ الإحلال تشير إليه.
          </span>
        </div>
      </div>

      {/*
        الحدُّ يُقال قبل أن يُصطدم به: خمسةٌ وعشرون بندًا في كلّ قائمة،
        ومن يكتب أكثرَ يردُّه الخادمُ بعد أن كتب.
      */}
      <p className="tiny mute mt-4">
        أقصى ما يُقبل <span className="num">{MAX_DUTIES}</span> بندًا في
        المهامّ ومثلُها في المؤهّلات، وكلُّ بندٍ ثلاثُ مئة حرفٍ على الأكثر.
      </p>
    </Modal>
  );
}
